import { IonButton, IonContent, IonFooter, IonHeader, IonImg, IonItem, IonModal, IonPage, IonRow, IonToolbar, useIonAlert } from '@ionic/react'
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../../services/redux/hook';
import { setUser } from '../../../services/redux/slices/user/user';

import './GenerateWallet.css'
import { createWalletAccount } from '../../../services/stellar/TransactionBuilder';
import AppHeader from '../../../components/AppHeader/AppHeader';
import { useEffect, useState } from 'react';
import { Asset, UserInfo, UserRegistrationParameters, UserRegistrationStatusRequestParameter, WalletInfoState, WalletKeysRegistrationParameters } from '../../../types/types';
import { ebioroApi, useRegisterUserMutation } from '../../../services/api/ebioro/ebioro';
import EbioroLoadingLogo from "../../../components/EbioroLoading/EbioroLoadingLogo";
import * as Sentry from "@sentry/react";
import { Networks } from "@stellar/stellar-sdk";
import { CapacitorKeyStore } from '../../../services/stellar/wallet-sdk/plugins/CapacitorStorage';
import { KeyManager, KeyManagerPlugins, KeyType } from '../../../services/stellar/wallet-sdk';
import { Key } from '../../../services/stellar/wallet-sdk/types';

const isTestnet = import.meta.env.VITE_isTestnet === 'true' ? true : false;

const GenerateWalletIntroPage: React.FC<any> = () => {

    const history = useHistory();
    const dispatch = useAppDispatch();
    const { t, i18n } = useTranslation();
    const user: UserInfo = useAppSelector((state) => state.user);
    const walletInfo: WalletInfoState = useAppSelector((state) => state.walletInfo);
    const pin: string = useAppSelector((state) => state.pin);
    const secureStorage = new CapacitorKeyStore();

    const keyManager = new KeyManager({ keyStore: secureStorage });

    keyManager.registerEncrypter(KeyManagerPlugins.ScryptEncrypter);
    keyManager.setDefaultNetworkPassphrase(isTestnet ? Networks.TESTNET : Networks.PUBLIC);
    const supportedAssets: Asset[] = useAppSelector((state) => state.supportedAssets.supportedAssets);

    const account_key: Key = {
        id: 'account_key',
        publicKey: walletInfo.account_key as string,
        privateKey: walletInfo.account_private_key as string,
        type: KeyType.plaintextKey,
        network: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
    };

    const device_key: Key = {
        id: 'device_key',
        publicKey: walletInfo.device_key as string,
        privateKey: walletInfo.device_private_key as string,
        type: KeyType.plaintextKey,
        network: isTestnet ? Networks.TESTNET : Networks.PUBLIC,
    };

    const keys: Key[] = [account_key, device_key];

    const [registerUser] = useRegisterUserMutation();
   
    const [present] = useIonAlert();
    const [authToken, setAuthToken] = useState<string>();

    const [presentLoader, setPresentLoader] = useState(false);

    const presentErrorAlert = (error: any) => {

        present({
            header: 'Error',
            message: `${error.data}`,
            buttons: [
                {
                    text: 'Close',
                    role: 'cancel',
                    handler: () => {
                        return
                    }
                }
            ],

        })

    }

    const goToSecureWalletInfoPage = async () => {

        try {

            setPresentLoader(false);

            history.push({
                pathname: '/signup/secure-wallet-info',
                state: {
                    detail: {
                        auth_token: authToken
                    }
                }
            })

        } catch (error) {

            Sentry.captureException(error);
            setPresentLoader(false);
            presentErrorAlert({ data: t('application.uncaught.ERROR') });
            return

        }
    }

    const generateWallet = async () => {

        const supportedTrustLineAssets = supportedAssets.filter((asset) => {

            return (asset.code === 'USDC')
        })

        setPresentLoader(true);

        const [error, stellarTx] = await createWalletAccount(walletInfo.account_private_key as string, walletInfo.device_private_key as string, supportedTrustLineAssets);

        if (stellarTx) {

            const params: UserRegistrationParameters = {

                first_name: user.first_name,
                last_name: user.last_name,
                email: user.email,
                is_email_verified: user.is_email_verified,
                mobile_number: user.mobile_number,
                is_mobile_number_verified: user.is_mobile_number_verified,
                calling_code: user.calling_code,
                auth_token: authToken as string,
                stellar_account: walletInfo.account_key as string,
                txEnvelope: stellarTx.toEnvelope().toXDR('base64')

            }

            try {

                const registeredUser = await registerUser(params).unwrap();

                const id = registeredUser.id;

                // Update user state with the ID returned from registration
                dispatch(setUser({
                    ...user,
                    id: id
                }));

                let status = registeredUser.status;

                while (status != 'COMPLETED' && status != 'ERROR') {

                    const params: UserRegistrationStatusRequestParameter = {
                        id: id
                    }

                    const statusResponse = await dispatch(ebioroApi.endpoints.getUserRegistrationStatus.initiate(params, { forceRefetch: true })).unwrap();

                    status = statusResponse.status;

                    await new Promise(r => setTimeout(r, 3000));

                }

                if (status === 'ERROR') {

                    presentErrorAlert({ data: t('application.uncaught.ERROR') });
                    setPresentLoader(false);
                    return;

                }

                await encryptKeys();

                goToSecureWalletInfoPage();

            } catch (error) {
                Sentry.captureException(error);
                setPresentLoader(false);
                presentErrorAlert({ data: t('application.uncaught.ERROR') });
                return

            }

        }

        setPresentLoader(false)

    }


    const encryptKeys = async () => {

        for (let i = 0; i < keys.length; i++) {

            await keyManager
                .storeKey({
                    key: keys[i],
                    password: pin,
                    encrypterName: KeyManagerPlugins.ScryptEncrypter.name,
                })
        }

    }


    useEffect(() => {

        const location: any = history.location;
        const authToken = location.state.detail.auth_token;
        setAuthToken(authToken);

    }, [])

    return (
        <IonPage>

            <IonHeader className="ion-no-border">

                <AppHeader 
                    title={t('generate-wallet-intro.HEADER') as string} 
                    goBack={true}
                    />

            </IonHeader>

            <IonContent scrollY={false}>

                <IonModal isOpen={presentLoader}>
                    <IonContent fullscreen={true} scrollY={false} className="generateWalletPageWaitModal_content_background">
                        <div className="ion-text-center">
                            <div className="generateWalletPageWaitModal_logo-on-center">

                                <EbioroLoadingLogo

                                    icon_color="white"
                                    mainText={t('generate-wallet-WAIT-MESSAGE-MAIN')}
                                    mainTextColor="light"
                                    subText={t('generate-wallet-WAIT-MESSAGE-SUB') as string}
                                    subTextColor="light"
                                />
                            </div>

                        </div>

                    </IonContent>
                </IonModal>

                <div >
                    <IonRow className="row2">
                        <IonImg src={'/assets/img/add_euros.svg'} className="recovery_intro_page_icon" />
                    </IonRow>


                    <IonItem className="intro-content" lines='none' >
                        <p>{t('generate-wallet-intro.CONTENT')}</p>
                    </IonItem>
                </div>



            </IonContent>

            <IonFooter className="ion-no-border">
                <IonToolbar>
                    <IonButton
                        color='primary'
                        type="submit"
                        onClick={() => generateWallet()}
                        expand="block"
                        id='pageFooterButton'
                    >
                        {/* {isSpinner?(<IonSpinner className="BUTTON-SPINNER"></IonSpinner>)
                : */}
                        {t('generate-wallet-intro.BTN_CONT')}
                    </IonButton>
                </IonToolbar>

            </IonFooter>

        </IonPage>
    )
}

export default GenerateWalletIntroPage